import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Leads
export const getLeads = (params) => api.get('/leads', { params });
export const createLead = (data) => api.post('/leads', data);
export const getLead = (id) => api.get(`/leads/${id}`);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const createCustomer = (data) => api.post('/customers', data);
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);

// Quotations
export const getQuotations = (params) => api.get('/quotations', { params });
export const createQuotation = (data) => api.post('/quotations', data);
export const getQuotation = (id) => api.get(`/quotations/${id}`);
export const updateQuotation = (id, data) => api.put(`/quotations/${id}`, data);
export const updateQuotationStatus = (id, status) => api.put(`/quotations/${id}/status?status=${status}`);
export const deleteQuotation = (id) => api.delete(`/quotations/${id}`);

// Bookings
export const getBookings = (params) => api.get('/bookings', { params });
export const createBooking = (data) => api.post('/bookings', data);
export const getBooking = (id) => api.get(`/bookings/${id}`);
export const updateBookingStatus = (id, status) => api.put(`/bookings/${id}/status?status=${status}`);
export const deleteBooking = (id) => api.delete(`/bookings/${id}`);

// Visa Services
export const getVisaServices = (params) => api.get('/visa-services', { params });
export const createVisaService = (data) => api.post('/visa-services', data);
export const getVisaService = (id) => api.get(`/visa-services/${id}`);
export const updateVisaStatus = (id, status, note = '') => api.put(`/visa-services/${id}/status?status=${status}&note=${encodeURIComponent(note)}`);
export const deleteVisaService = (id) => api.delete(`/visa-services/${id}`);

// Payments
export const getPayments = (params) => api.get('/payments', { params });
export const createPayment = (data) => api.post('/payments', data);
export const getPayment = (id) => api.get(`/payments/${id}`);

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const generateInvoice = (bookingId) => api.post(`/invoices/generate/${bookingId}`);
export const downloadInvoicePdf = (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });

// WhatsApp
export const getWhatsAppMessages = (params) => api.get('/whatsapp/messages', { params });
export const sendWhatsAppMessage = (data) => api.post('/whatsapp/send', data);
export const getWhatsAppTemplates = () => api.get('/whatsapp/templates');

// Reports
export const getSalesReport = (params) => api.get('/reports/sales', { params });
export const getLeadsReport = () => api.get('/reports/leads');
export const getBookingsReport = () => api.get('/reports/bookings');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Accounts
export const getAccountsOverview = () => api.get('/accounts/overview');
export const getIncome = (params) => api.get('/accounts/income', { params });
export const createIncome = (data) => api.post('/accounts/income', data);
export const deleteIncome = (id) => api.delete(`/accounts/income/${id}`);
export const getExpenses = (params) => api.get('/accounts/expenses', { params });
export const createExpense = (data) => api.post('/accounts/expenses', data);
export const deleteExpense = (id) => api.delete(`/accounts/expenses/${id}`);
export const getLedger = (params) => api.get('/accounts/ledger', { params });
export const getProfitLoss = (period) => api.get('/accounts/profit-loss', { params: { period } });

// Itineraries
export const getItineraries = (params) => api.get('/itineraries', { params });
export const createItinerary = (data) => api.post('/itineraries', data);
export const getItinerary = (id) => api.get(`/itineraries/${id}`);
export const updateItinerary = (id, data) => api.put(`/itineraries/${id}`, data);
export const deleteItinerary = (id) => api.delete(`/itineraries/${id}`);
export const downloadItineraryPdf = (id) => api.get(`/itineraries/${id}/pdf`, { responseType: 'blob' });
export const downloadItineraryDocx = (id) => api.get(`/itineraries/${id}/docx`, { responseType: 'blob' });

// Trips
export const getTrips = (params) => api.get('/trips', { params });
export const createTrip = (data) => api.post('/trips', data);
export const getTrip = (id) => api.get(`/trips/${id}`);
export const updateTrip = (id, data) => api.put(`/trips/${id}`, data);
export const deleteTrip = (id) => api.delete(`/trips/${id}`);

// Receipts
export const getReceipts = (params) => api.get('/receipts', { params });
export const generateReceipt = (paymentId) => api.post('/receipts/generate', { payment_id: paymentId });
export const getReceipt = (id) => api.get(`/receipts/${id}`);
export const getReceiptPdf = (id) => api.get(`/receipts/${id}/pdf`, { responseType: 'blob' });

// Accounts - Enhanced
export const getTripwiseExpenses = () => api.get('/accounts/tripwise');
export const getDailywiseExpenses = (params) => api.get('/accounts/dailywise', { params });

// Export functions
export const exportData = (module, format) => {
  return api.get(`/export/${module}/${format}`, { responseType: 'blob' });
};

// Import functions
export const importData = (module, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/import/${module}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getImportTemplate = (module) => {
  return api.get(`/import/template/${module}`, { responseType: 'blob' });
};

export default api;
